import React, { useEffect, useState } from 'react';

import moment from 'moment';



import { IOrderRevisionLogEntry } from '../../store/order/orderStore';

import {

    ActivityLineDetail,

    getActivityLineDetails,

    getActivitySummary,

    getActorInitial,

    getActorPrimary,

    getActorSecondary,

    getDistinctContributors,

    entryMatchesContributor,

    resolveActivityLineThumbnailUrl,

    targetTypeLabel,

} from '../../utils/orderActivityUtils';



export interface OrderActivityPanelProps {

    entries: IOrderRevisionLogEntry[];

    isLoading?: boolean;

    title?: string;

    subtitle?: string;

    emptyMessage?: string;

    maxEntries?: number;

    onLineClick?: (lineId: number | null) => void;

    showContributors?: boolean;

    showProductThumbnails?: boolean;

    lineNavigationMode?: 'click' | 'button';

    lineNavigationLabel?: string;

    className?: string;

    showHeader?: boolean;

    showActorCustomerName?: boolean;

    compactLineLabels?: boolean;

    enableContributorFilter?: boolean;

}



const DEFAULT_MAX = 50;

const formatLineOverview = (line: ActivityLineDetail) => {
    const qtySuffix = line.quantity != null && line.quantity > 0 ? ` · qty ${line.quantity}` : '';
    return `${line.label}${qtySuffix}`;
};



const LineThumbnail: React.FC<{ line: ActivityLineDetail }> = ({ line }) => {

    const [hidden, setHidden] = useState(false);

    const url = resolveActivityLineThumbnailUrl(line);



    if (!url || hidden) {

        return <span className="order-activity-panel__thumb order-activity-panel__thumb--placeholder" aria-hidden />;

    }



    return (

        <img

            className="order-activity-panel__thumb"

            src={url}

            alt=""

            loading="lazy"

            onError={() => setHidden(true)}

        />

    );

};



const OrderActivityPanel: React.FC<OrderActivityPanelProps> = ({

    entries,

    isLoading = false,

    title = 'Order activity',

    subtitle = 'Who changed what on this order',

    emptyMessage = 'No changes recorded for this order.',

    maxEntries = DEFAULT_MAX,

    onLineClick,

    showContributors = false,

    showProductThumbnails = false,

    lineNavigationMode = 'click',

    lineNavigationLabel = 'Go to line in order',

    className = '',

    showHeader = true,

    showActorCustomerName = false,

    compactLineLabels = false,

    enableContributorFilter = false,

}) => {

    const [selectedContributorLogin, setSelectedContributorLogin] = useState<string | null>(null);

    useEffect(() => {
        setSelectedContributorLogin(null);
    }, [entries]);

    const contributors = showContributors ? getDistinctContributors(entries) : [];

    const contributorFilterEnabled = enableContributorFilter && contributors.length > 1;

    const filteredEntries = selectedContributorLogin
        ? entries.filter((entry) => entryMatchesContributor(entry, selectedContributorLogin))
        : entries;

    const visibleEntries = filteredEntries.slice(0, maxEntries);

    const displayEmptyMessage =
        selectedContributorLogin && !isLoading && filteredEntries.length === 0
            ? 'No changes from this user.'
            : emptyMessage;

    const useLineButton = lineNavigationMode === 'button';

    const handleContributorClick = (login: string) => {
        setSelectedContributorLogin((current) =>
            current && current.toLowerCase() === login.toLowerCase() ? null : login
        );
    };

    const isContributorActive = (login: string) =>
        !!selectedContributorLogin &&
        selectedContributorLogin.toLowerCase() === login.toLowerCase();



    const handleLineNavigate = (lineId: number | null) => {

        if (!lineId || !onLineClick) {

            return;

        }



        onLineClick(lineId);

    };



    const renderLineRow = (line: ActivityLineDetail, key: string, clickable: boolean) => {

        const content = (

            <>

                {showProductThumbnails && <LineThumbnail line={line} />}

                <span className="order-activity-panel__line-body">

                    <span className="order-activity-messenger__line-label">{line.label}</span>

                    {line.quantity != null && line.quantity > 0 && (

                        <span className="order-activity-messenger__line-qty">qty {line.quantity}</span>

                    )}

                </span>

            </>

        );



        if (clickable && line.lineId && onLineClick) {

            return (

                <button

                    key={key}

                    type="button"

                    className="order-activity-messenger__line-item order-activity-messenger__line-item--clickable order-activity-panel__line-row"

                    onClick={(event) => {

                        event.stopPropagation();

                        handleLineNavigate(line.lineId);

                    }}

                >

                    {content}

                </button>

            );

        }



        return (

            <div key={key} className="order-activity-messenger__line-item order-activity-panel__line-row">

                {content}

            </div>

        );

    };



    const renderLineNavigationButton = (lineId: number | null) => {

        if (!useLineButton || !lineId || !onLineClick) {

            return null;

        }



        return (

            <button

                type="button"

                className="order-activity-panel__line-go"

                onClick={() => handleLineNavigate(lineId)}

            >

                {lineNavigationLabel}

            </button>

        );

    };



    const renderLineDetails = (entry: IOrderRevisionLogEntry) => {

        const lineDetails = getActivityLineDetails(entry);



        if (lineDetails.length > 1) {

            return (

                <ul className="order-activity-messenger__lines">

                    {lineDetails.map((line, index) => (

                        <li key={`${entry.id}-line-${index}`} className="order-activity-panel__line-block">

                            {renderLineRow(line, `${entry.id}-line-${index}`, !useLineButton)}

                            {renderLineNavigationButton(line.lineId)}

                        </li>

                    ))}

                </ul>

            );

        }



        if (lineDetails.length === 1) {

            const line = lineDetails[0];



            return (

                <div className="order-activity-panel__single-line">

                    <div className="order-activity-panel__line-row order-activity-panel__line-row--summary">

                        {showProductThumbnails && <LineThumbnail line={line} />}

                        <span className="order-activity-messenger__text">
                            {compactLineLabels ? formatLineOverview(line) : getActivitySummary(entry)}
                        </span>

                    </div>

                    {renderLineNavigationButton(line.lineId)}

                </div>

            );

        }



        return (

            <span className="order-activity-messenger__text">
                {compactLineLabels && lineDetails.length === 1
                    ? formatLineOverview(lineDetails[0])
                    : getActivitySummary(entry)}
            </span>

        );

    };



    const renderMessage = (entry: IOrderRevisionLogEntry) => {

        const actorEmail = getActorPrimary(entry);

        const customerName = getActorSecondary(entry);

        const initial = getActorInitial(entry);

        const timeLabel = moment(entry.changedAtUtc).format('DD MMM YYYY HH:mm');

        const lineDetails = getActivityLineDetails(entry);

        const primaryLineId = lineDetails.length === 1 ? lineDetails[0].lineId : null;

        const clickable = !useLineButton && !!primaryLineId && !!onLineClick;



        return (

            <div

                key={`${entry.id}-${entry.revisionNumber}`}

                className={`order-activity-messenger__message${clickable ? ' order-activity-messenger__message--clickable' : ''}`}

                onClick={clickable ? () => onLineClick!(primaryLineId) : undefined}

                role={clickable ? 'button' : undefined}

                tabIndex={clickable ? 0 : undefined}

            >

                <span className="order-activity-messenger__avatar" aria-hidden>{initial}</span>

                <span className="order-activity-messenger__bubble">

                    <span className="order-activity-messenger__bubble-header">

                        <span className="order-activity-messenger__actor-block">

                            <span className="order-activity-messenger__actor">{actorEmail}</span>

                            {showActorCustomerName && customerName && (

                                <span className="order-activity-messenger__actor-customer">{customerName}</span>

                            )}

                        </span>

                        <span className="order-activity-messenger__time">{timeLabel}</span>

                    </span>

                    {renderLineDetails(entry)}

                    <span className="order-activity-messenger__tag">{targetTypeLabel(entry.targetType)}</span>

                </span>

            </div>

        );

    };



    return (

        <div className={`order-activity-panel ${className}`.trim()}>

            {showHeader && (
                <div className="order-activity-messenger__panel-header">
                    <div>
                        <h4 className="order-activity-messenger__title">{title}</h4>
                        {subtitle && <p className="order-activity-messenger__subtitle">{subtitle}</p>}
                    </div>
                </div>
            )}



            {showContributors && contributors.length > 0 && (

                <div className="order-history-contributors">

                    <span className="order-history-contributors__label">Contributors:</span>

                    {contributors.map((contributor) => {
                        if (contributorFilterEnabled) {
                            const isActive = isContributorActive(contributor.login);

                            return (
                                <button
                                    key={contributor.login}
                                    type="button"
                                    className="order-history-contributors__chip order-history-contributors__chip--filter"
                                    aria-pressed={isActive}
                                    onClick={() => handleContributorClick(contributor.login)}
                                >
                                    <span className="order-history-contributors__check" aria-hidden>
                                        [{isActive ? 'X' : ' '}]
                                    </span>
                                    <span className="order-history-contributors__name">{contributor.label}</span>
                                </button>
                            );
                        }

                        return (
                            <span key={contributor.login} className="order-history-contributors__chip">
                                {contributor.label}
                            </span>
                        );
                    })}

                </div>

            )}



            <div className="order-activity-messenger__messages order-activity-panel__messages">

                {isLoading && !visibleEntries.length && (

                    <p className="order-activity-messenger__empty">Loading activity…</p>

                )}

                {!isLoading && !visibleEntries.length && (

                    <p className="order-activity-messenger__empty">{displayEmptyMessage}</p>

                )}

                {visibleEntries.map(renderMessage)}

            </div>

        </div>

    );

};



export default OrderActivityPanel;

